import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { formatDistanceToNowStrict } from 'date-fns'

import Loading from '@/shared/Loading'
import NotifyLevelMenu from '@/shared/NotifyLevelMenu'
import useQuery from '@/hooks/useQuery'

// Landing spot for the chat icon in the sidebar: every room the user can see,
// grouped so the list stays readable once someone has a dozen cohorts behind
// them. Archived rooms collapse into a single disclosure at the bottom, since
// a graduate accumulates one per course they have ever taken and those would
// otherwise bury the rooms they are actually in.
//
// Rooms are gated, so this list holds two kinds of row: ones the user is in,
// and ones they may join. The second kind is the entire discovery path for a
// gated room, which is why they are listed rather than hidden.
//
// Each row carries the same notification-level menu as the room header,
// because the room you want to quiet is usually one you are not sitting in,
// and going to find it just to silence it is how people end up unsubscribing
// from chat email altogether.

// Order matters: community rooms are the ones we want people to find.
const SECTIONS = [
  { kind: 'general', title: 'Community', blurb: 'Open to everyone who has taken a course with us.' },
  { kind: 'members', title: 'Members Only', blurb: 'Included with Closing Credits membership.' },
  { kind: 'cohort',  title: 'Your Course Chats', blurb: null },
]

// Long enough that typing a course name is one request, short enough that the
// list doesn't feel stuck behind the keystroke.
const FILTER_DEBOUNCE_MS = 250

const RoomIcon = ({ room }) => (
  <div className='shrink-0 h-11 w-11 rounded-xl overflow-hidden bg-clcpurple/15 flex items-center justify-center'>
    {room.imageUrl
      ? <img src={room.imageUrl} alt='' className='h-full w-full object-cover' />
      : <span className='text-clcpurple font-bold'>#</span>}
  </div>
)

// The subtitle is the cohort's moniker, "Wed 8pm ET - Cohort 9 (Aug 2026)",
// and in the cohort section it is the only thing telling two rooms apart: the
// name is the course, so a course with three cohorts is three identical rows
// without it. It therefore always shows, with activity on its own line rather
// than replacing it.
const Meta = ({ room }) => {
  const activity = room.lastMessageAt
    ? `Active ${formatDistanceToNowStrict(new Date(room.lastMessageAt), { addSuffix: true })}`
    : null

  // A locked room's activity is not the reader's to see, so it stays off the
  // row and the subtitle carries the reason instead.
  if (room.locked) return <p className='text-xs text-slate-500 truncate'>{room.subtitle || 'Members only'}</p>

  return (
    <>
      <p className='text-xs text-slate-500 truncate'>
        {room.subtitle || activity || 'No messages yet, start the conversation'}
      </p>
      {room.subtitle && (
        <p className='text-[11px] leading-4 text-slate-400 truncate'>
          {activity || 'No messages yet, start the conversation'}
        </p>
      )}
    </>
  )
}

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'text-slate-400 bg-slate-100 dark:bg-slate-800',
    purple: 'text-clcpurple bg-clcpurple/10',
  }
  return (
    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tones[tone]}`}>
      {children}
    </span>
  )
}

// A locked room is deliberately still rendered: a room you can see and not
// enter is an offer, a room that is not there is nothing. Neither locked nor
// joinable rooms get an unread badge or a notification menu, because both
// need a membership the reader does not have yet, and a badge you cannot
// clear teaches people to ignore badges everywhere else.
const RoomRow = ({ room, onNotifyLevel, onJoin, joiningSlug }) => {
  const joining = joiningSlug === room.slug
  const entered = room.accessLevel === 'full'
  const unread = entered && !room.muted ? room.unreadCount : 0

  const body = (
    <>
      <RoomIcon room={room} />
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <p className='text-sm font-bold text-slate-900 dark:text-slate-100 truncate'>{room.name}</p>
          {room.locked && <Pill tone='purple'>Members</Pill>}
          {room.archived && <Pill>Archived</Pill>}
          {entered && room.muted && <Pill>Muted</Pill>}
        </div>
        <Meta room={room} />
      </div>
    </>
  )

  if (room.locked) {
    return (
      <div
        className='flex items-center gap-3 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 px-4 py-3 opacity-60 cursor-not-allowed'
        aria-disabled='true'
        title='Closing Credits members only'
      >
        {body}
      </div>
    )
  }

  // Joining goes straight into the room. Making someone click Join and then
  // click the room they just joined is a step that exists only because the
  // membership row does.
  if (room.joinable) {
    return (
      <div className='flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 px-4 py-3'>
        {body}
        <button
          type='button'
          onClick={() => onJoin(room)}
          disabled={joining}
          className='shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-clcpurple hover:bg-clcpurple-dark disabled:opacity-50'
        >
          {joining ? 'Joining…' : 'Join'}
        </button>
      </div>
    )
  }

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition ${
        unread > 0
          ? 'border-clcpurple/30 bg-clcpurple/[0.06] dark:bg-clcpurple/10'
          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-clcpurple/30'
      }`}
    >
      {/* The menu is a sibling of the link rather than inside it: a button
          nested in an anchor is invalid and swallows keyboard navigation. */}
      <Link to={room.path} className='flex min-w-0 flex-1 items-center gap-4 hover:no-underline'>
        {body}
      </Link>

      {unread > 0 && (
        <span className='shrink-0 h-6 min-w-[1.5rem] px-2 rounded-full bg-clcpurple text-white text-xs font-bold flex items-center justify-center'>
          {unread > 99 ? '99+' : unread}
        </span>
      )}

      <NotifyLevelMenu
        compact
        level={room.notifyLevel || 'all'}
        onChange={(level) => onNotifyLevel(room, level)}
      />
    </div>
  )
}

const Section = ({ title, blurb, count, rooms, children, ...rowProps }) => {
  if (rooms.length === 0 && !children) return null
  return (
    <section className='mb-8'>
      <h2 className='px-1 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
        {title} <span className='font-normal text-slate-400'>{count ?? rooms.length}</span>
      </h2>
      {blurb && <p className='px-1 mt-0.5 mb-2 text-xs text-slate-400'>{blurb}</p>}
      {children}
      <ul className={`space-y-2 ${blurb || children ? '' : 'mt-2'}`}>
        {rooms.map(room => (
          <li key={room.slug}>
            <RoomRow room={room} {...rowProps} />
          </li>
        ))}
      </ul>
    </section>
  )
}

const ChatsIndex = () => {
  const { getRequest, putpostRequest } = useQuery()
  // useQuery rebuilds these every render. Closing over them directly makes
  // `load` a new function each time, which re-fires the effect that depends
  // on it, which sets state, which renders again: the chats page fired the
  // index endpoint in a tight loop. The ref keeps `load` stable.
  const apiRef = useRef({ getRequest, putpostRequest })
  apiRef.current = { getRequest, putpostRequest }
  const history = useHistory()
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [joiningSlug, setJoiningSlug] = useState(null)

  // Only the cohort group pages. Community rooms number eight and legacy
  // course rooms are a closed set, but cohort rooms are one per cohort
  // forever, and site staff see every one of them.
  const [filter, setFilter] = useState('')
  const [appliedFilter, setAppliedFilter] = useState('')
  const [cohortPage, setCohortPage] = useState(1)
  const [cohortTotal, setCohortTotal] = useState(0)
  const [cohortHasMore, setCohortHasMore] = useState(false)
  const firstLoad = useRef(true)

  const load = useCallback((page, query, { append }) => {
    // Params go through getRequest's second argument, which builds the query
    // string itself. Interpolating them into the path gives you two ?s.
    const params = { cohort_page: String(page) }
    if (query) params.cohort_q = query

    apiRef.current.getRequest('/api/v1/channels', params, (err, data) => {
      setLoading(false)
      setLoadingMore(false)
      if (err) return

      const incoming = data.rooms || []
      // Appending replaces only the cohort group: the other sections come
      // back whole on every request and re-adding them would duplicate them.
      setRooms(prev => (append
        ? [...prev, ...incoming.filter(r => r.kind === 'cohort')]
        : incoming))
      setCohortTotal(data.cohortTotal || 0)
      setCohortHasMore(!!data.cohortHasMore)
    })
  }, [])

  useEffect(() => { load(1, '', { append: false }) }, [load])

  // Debounced filter. Skipped on the first render so it can't fire a second
  // identical request behind the initial load.
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return }

    const timer = setTimeout(() => {
      setAppliedFilter(filter)
      setCohortPage(1)
      load(1, filter, { append: false })
    }, FILTER_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [filter, load])

  const loadMore = () => {
    const next = cohortPage + 1
    setCohortPage(next)
    setLoadingMore(true)
    load(next, appliedFilter, { append: true })
  }

  const { live, archived } = useMemo(() => ({
    live: rooms.filter(r => !r.archived),
    archived: rooms.filter(r => r.archived),
  }), [rooms])

  const join = (room) => {
    setJoiningSlug(room.slug)
    putpostRequest(`/api/v1/channels/by_slug/${room.slug}/join`, 'POST', {}, (err) => {
      setJoiningSlug(null)
      if (err) return
      history.push(room.path)
    })
  }

  // Keyed by slug rather than channel id: a room the user has not opened may
  // have no id in hand, and the endpoint resolves the slug either way.
  const setNotifyLevel = (room, level) => {
    const previous = room.notifyLevel
    const apply = (value, id) => setRooms(prev => prev.map(r => (
      r.slug === room.slug
        ? { ...r, id: id || r.id, notifyLevel: value, muted: value === 'none' }
        : r
    )))
    apply(level)
    putpostRequest(`/api/v1/channels/by_slug/${room.slug}/notify_level`, 'POST', { level }, (err, data) => {
      if (err) { apply(previous); return }
      apply(data.channel.notifyLevel, data.channel.id)
    })
  }

  const rowProps = { onNotifyLevel: setNotifyLevel, onJoin: join, joiningSlug }

  if (loading) return <Loading />

  if (rooms.length === 0 && !appliedFilter) {
    return (
      <div className='py-8'>
        <div className='rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 py-12 text-center'>
          <p className='text-sm text-slate-600 dark:text-slate-400'>
            You're not in any chats yet. Enrol in a course and its chat shows up here.
          </p>
          <Link to='/courses' className='mt-4 inline-block text-sm font-semibold text-clcpurple hover:text-clcpurple-dark'>
            Browse courses →
          </Link>
        </div>
      </div>
    )
  }

  const cohortRooms = live.filter(r => r.kind === 'cohort')
  // The box stays put once there is anything to filter, including when the
  // current filter matches nothing. Hiding it on an empty result would strand
  // whoever typed the query with no way to clear it.
  const showFilter = cohortTotal > 0 || !!appliedFilter || cohortRooms.length > 0

  const cohortControls = showFilter ? (
    <div className='mt-2 mb-3'>
      <input
        type='search'
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder='Filter by course or cohort'
        aria-label='Filter course chats'
        className='w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-clcpurple focus:outline-none'
      />
    </div>
  ) : null

  return (
    <div className='py-8'>
      {SECTIONS.map(section => {
        if (section.kind !== 'cohort') {
          return (
            <Section
              key={section.kind}
              title={section.title}
              blurb={section.blurb}
              rooms={live.filter(r => r.kind === section.kind)}
              {...rowProps}
            />
          )
        }

        return (
          <Section
            key={section.kind}
            title={section.title}
            blurb={section.blurb}
            count={cohortTotal}
            rooms={cohortRooms}
            {...rowProps}
          >
            {cohortControls}
            {cohortRooms.length === 0 && appliedFilter && (
              <p className='px-1 py-3 text-sm text-slate-500'>
                No course chats match “{appliedFilter}”.
              </p>
            )}
          </Section>
        )
      })}

      {cohortHasMore && (
        <div className='-mt-4 mb-8'>
          <button
            type='button'
            onClick={loadMore}
            disabled={loadingMore}
            className='w-full rounded-xl border border-slate-200 dark:border-slate-800 px-4 py-2.5 text-sm font-semibold text-clcpurple hover:border-clcpurple/30 disabled:opacity-50'
          >
            {loadingMore ? 'Loading…' : `Show more course chats (${cohortTotal - cohortRooms.length} left)`}
          </button>
        </div>
      )}

      {archived.length > 0 && (
        <section>
          <button
            type='button'
            onClick={() => setShowArchived(!showArchived)}
            aria-expanded={showArchived}
            className='flex w-full items-center gap-2 px-1 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-clcpurple'
          >
            <svg
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-hidden='true'
              className={`h-3.5 w-3.5 transition-transform ${showArchived ? 'rotate-90' : ''}`}
            >
              <path d='M7.21 5.21a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 11-1.06-1.06L10.94 10 7.21 6.27a.75.75 0 010-1.06z' />
            </svg>
            Archived <span className='font-normal text-slate-400'>{archived.length}</span>
          </button>
          {showArchived && (
            <ul className='mt-2 space-y-2'>
              {archived.map(room => (
                <li key={room.slug}>
                  <RoomRow room={room} {...rowProps} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}

export default ChatsIndex
